import { FinishRegisterUserCommand, LocalizedLinkDisplayModel } from '@/api/model-index';
import { AccessToken } from '@/core/models/access-token.model';
import { IdToken } from '@/core/models/id-token.model';
import { Component, OnInit } from '@angular/core';
import { AbstractControl, ValidationErrors, Validators } from '@angular/forms';
import { JwtHelperService } from '@auth0/angular-jwt';
import { finalize, map } from 'rxjs/operators';
import { PasswordValidator } from '../../validators/password.validator';
import { PasswordMatchValidator } from '../../validators/passwordmatch.validator';
import { RegisterComponent } from '../register/register.component';
import { emailValidator } from '@/core/validators/customform.validators';

@Component({
  selector: 'finish-register',
  templateUrl: './finish-register.component.html',
  styleUrls: ['./finish-register.component.scss']
})
export class FinishRegisterComponent extends RegisterComponent implements OnInit {
  protected jwtHelper = new JwtHelperService();
  registerCommand = new FinishRegisterUserCommand();

  public override ngOnInit(): void {
    this.loadAnreden();

    this.route.queryParams.subscribe((params) => {
      try {
        let token = atob(params.token);
        let decodedToken: any = this.jwtHelper.decodeToken(token);

        this.registerCommand.verificationToken = token;
        this.registerCommand.email = decodedToken.email;

        this.registrationForm.controls.anredeId.setValue(decodedToken.anredeid);
        this.registrationForm.controls.titelId.setValue(decodedToken.titelid);
        this.registrationForm.controls.vorname.setValue(decodedToken.firstname);
        this.registrationForm.controls.nachname.setValue(decodedToken.lastname);
        this.registrationForm.controls.email.setValue(decodedToken.email);

        this.registrationForm.controls.anredeId.disable();
        this.registrationForm.controls.titelId.disable();
        this.registrationForm.controls.vorname.disable();
        this.registrationForm.controls.nachname.disable();

        if (decodedToken.doubleOptinCompleted == true) {
          this.isPasswordFieldVisible = false;
        }
      } catch (error) {
        this.router.navigateByUrl("/authentication/preregister");
        throw error;
      }
    });

    this.languageSubscription = this.languageService.languageChanged$.subscribe(() => {
      this.loadAnreden();
    });
  }

  protected override createForm() {
    this.registrationForm = this.formBuilder.group({
      anredeId: ['', []],
      titelId: ['', []],
      email: ['', [Validators.required, emailValidator]],
      passwort: ['', [Validators.required, Validators.minLength(8), new PasswordValidator(this.languageService)]],
      vorname: ['', [Validators.required, Validators.pattern(/^(\s+\S+\s*)*(?!\s).*$/)]],
      nachname: ['', [Validators.required, Validators.pattern(/^(\s+\S+\s*)*(?!\s).*$/)]],
      passwortWiederholen: ['', [Validators.required]],
      datenschutz: ['', [Validators.requiredTrue]]
    }, { validators: PasswordMatchValidator("passwort", "passwortWiederholen") });
  }

  protected get isRegistrationButtonDisabled(): boolean {
    if (this.isPasswordFieldVisible) {
      return this.registrationForm.invalid || this.isLoading;
    }
    return !this.registrationForm.controls.datenschutz.value || this.isLoading;
  }

  public override register() {
    this.registerCommand.password = this.registrationForm.controls.passwort.value;
    this.registerCommand.scope = this.authenticationConfig.scope;
    this.registerCommand.sprache = this.languageService.GetCurrLanguageAsEnum();
    
    this.registerSubscription = this.authenticationService.apiAuthenticationFinishRegisterPost(this.registerCommand.AsRequestBody())
      .pipe(map(idToken => {
        if (idToken)
          this.authenticationService.saveIdToken(new IdToken(idToken));

        return;
      }))
      .pipe(finalize(() => {
        sessionStorage.removeItem("VerificationToken");
        this.registrationForm.markAsPristine();
      }))
      .subscribe(result => {
        let promise = this.router.navigateByUrl(this.authenticationConfig.defaultRedirectUrl);

      this.loadingAnimationService.showSpinnerUsingPromise(promise);
    }, (error => {
      let message = this.languageService.instant('smth_didntwork');

        if (error && error.errorMessages && error.errorMessages.length > 0) {
          message = error.errorMessages[0];
        }

        this.notificationService.show({
          content: message,
          animation: { type: "fade", duration: 500 },
          position: { horizontal: "center", vertical: "bottom" },
          type: { style: "error", icon: true },
        });
      }));

    this.loadingAnimationService.showSpinnerUsingSubscription(this.registerSubscription);
  }

  public get linkDatenschutzbestimmung(): string{
    let datenschutz: LocalizedLinkDisplayModel | undefined = this.languageService.getCurrentLocalizedTextDisplayModel(this.portalkonfiguration.datenschutzbestimmungen) ?? this.languageService.instant('undefinedText');

    return datenschutz?.link ?? "";
  };

  public get datenschutzErrorMessages(): string[] {
    let validationErrors: ValidationErrors | null = this.datenschutzFormControl.errors;

    if (!validationErrors)
      return [];

    return this.toDatenschutzErrorMessage(validationErrors);
  }

  protected toDatenschutzErrorMessage(validationErrors: ValidationErrors): string[] {
    let errorMessages: string[] = [];

    for (let key in validationErrors) {
      switch (key) {
        case "required":
          errorMessages.push(this.languageService.instant("register.privacypolicy_required"));
          break;
        default:
          errorMessages.push(this.languageService.instant("register.invalid"));
      }
    }

    return errorMessages;
  }

  public get datenschutzFormControl(): AbstractControl {
    return this.registrationForm.controls["datenschutz"];
  }
}
